import React, { useEffect, useState } from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MODEL_TYPE_CAREER_CONTENT,
  MODEL_TYPE_DASHBOARD,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import {
  convertToUnderscore,
  getBannerImage,
  getColorCode,
  getCookie,
  getImage,
  getIndustryImage,
} from "../../utils/utility-functions";

import Loader from "../../components/loader";
import DisclaimerPopup from "../../components/disclaimer-popup";
import { useDispatch } from "react-redux";
import FunnelStepTypes from "../funnel-step/types";
import Guid from "guid";

import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const pushResetSelectedAnswerCountToFunnelStep = () => ({
  type: FunnelStepTypes.RESET_SELECTED_ANSWER_COUNT,
});

const PushAssessmentAttemptIdToFunnelStep = (assessmentAttemptId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ATTEMPT_ID,
  assessmentAttemptId,
});
const PushQuizCompleteToFunnelStep = (quizComplete) => ({
  type: FunnelStepTypes.SET_QUIZ_COMPLETE,
  quizComplete,
});

const CareerPrecontent = ({
  getInsightfulContentConnect,
  funnelStep,
  getAssessmentAttemptByUserIdConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
}) => {
  const [insightfulContent, setInsightfulContent] = useState({});
  const { t } = useTranslation();
  const [insightfulFlavourText, setInsightfulFlavourText] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [userData, setUserData] = useState(null);
  const newAssessmentAttemptId = Guid.create().value;
  const [assessmentAttemptData, setAssessmentAttemptData] = useState({});
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const careerReportPath = PathHelper(
    APP_NAVIGATION.CAREER_REPORT,
    currentLocale
  );

  const dispatch = useDispatch();

  // const token = getCookie("jwtToken");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      setIsLoading(true);
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
      getAssessmentAttemptByUserIdConnect(userId, currentLocale)
        .then((response) => {
          const filterByCurrentAssessmentAttemptId = response.filter(
            (item) => item.assessmentAttemptId === assessmentAttemptId
          );
          setCurrentAssessmentAttemptData(filterByCurrentAssessmentAttemptId);
          const filterLatestPersonalityData = response
            .filter((item) => {
              return (
                item.assessmentId === PERSONALITY_TEST_ASSESSMENT_ID &&
                item.personality !== null
              );
            })
            ?.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)) // Sort by createdOn in descending order
            ?.map((item, index) => {
              if (index === 0) {
                return item;
              }
            })
            ?.filter(Boolean)[0];

          // Set the assessment attempt data
          setAssessmentAttemptData(filterLatestPersonalityData);

          // Filter the latest personality data
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [getAssessmentAttemptByUserIdConnect, userId]);

  useEffect(() => {
    if (userId && currentAssessmentAttemptData) {
      setIsLoading(true);
      const data = {
        modelType:
          MODEL_TYPE_CAREER_CONTENT[currentLocale] ||
          MODEL_TYPE_CAREER_CONTENT[LOCALE.EN],
        contentKey:
          currentAssessmentAttemptData[0]?.personality?.title.toLowerCase(),
      };
      getInsightfulContentConnect(data)
        .then((response) => {
          setInsightfulContent(response);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [userId, currentAssessmentAttemptData]);

  useEffect(() => {
    if (userId && currentAssessmentAttemptData) {
      setIsLoading(true);
      const data = {
        modelType: MODEL_TYPE_DASHBOARD[currentLocale],
        contentKey:
          currentAssessmentAttemptData[0]?.personality?.title.toLowerCase(),
      };
      getInsightfulContentConnect(data)
        .then((response) => {
          setInsightfulFlavourText(response);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [userId, currentAssessmentAttemptData]);
  const traitCode = insightfulContent?.content?.Code;
  const navigate = useNavigate();

  const handleViewMore = (industry) => {
    const contentKey = convertToUnderscore(traitCode + "_" + industry);
    navigate(
      `${careerReportPath.next}?code=${contentKey}&aai=${assessmentAttemptId}&uid=${userId}`
    );
  };
  const personalityGroup = assessmentAttemptData
    ? assessmentAttemptData?.personality?.role
    : "";
  const personalityName = assessmentAttemptData
    ? assessmentAttemptData?.personality?.name
    : "";

  const currentPersonalityGroup = currentAssessmentAttemptData
    ? currentAssessmentAttemptData[0]?.personality?.role
    : "";
  const currentPersonalityName = currentAssessmentAttemptData
    ? currentAssessmentAttemptData[0]?.nameEnglishCode
    : "";

  const personalityColorDark = getColorCode(
    currentPersonalityGroup && currentPersonalityGroup
  ).dark;

  const personalityColorStyle = getColorCode(
    currentPersonalityGroup && currentPersonalityGroup
  ).style;

  const bannerImage = getBannerImage(currentPersonalityGroup);
  const personalityImage = getImage(
    currentPersonalityGroup,
    currentPersonalityName,
    true,
    gender || userData?.gender
  );

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleTestClick = () => {
    dispatch(pushResetSelectedAnswerCountToFunnelStep());
    dispatch(PushAssessmentAttemptIdToFunnelStep(newAssessmentAttemptId));
    dispatch(PushQuizCompleteToFunnelStep(false));
  };

  if (isLoading) {
    return (
      <div>
        <Loader isLoading={isLoading} />
      </div>
    );
  }

  return (
    <>
      <Header
        personalityGroup={
          personalityGroup ? personalityGroup.toLowerCase() : ""
        }
        personalityName={personalityName ? personalityName.toLowerCase() : ""}
      />
      <BackToTopButton />

      <div className="bg-gray-100">
        <div className="container mx-auto p-4 sm:p-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="relative h-64 sm:h-96 bg-gradient-to-r p-10">
              <img
                src={bannerImage}
                alt="Personality Background"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              {/* <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-purple-500 to-transparent opacity-75"></div> */}
              <div className="absolute inset-0 flex items-center justify-between">
                <div className="w-full sm:w-3/5 pl-8">
                  <h1 className="text-4xl md:text-4xl font-bold mb-2 shadow-text text-white">
                    {insightfulContent && insightfulContent?.content?.Code}
                  </h1>

                  <h4 className="text-xl lg:text-3xl font-light shadow-text text-white">
                    {insightfulFlavourText &&
                      insightfulFlavourText?.content?.Content?.centerheading}
                  </h4>
                </div>
                <div className="hidden sm:block w-max h-full aspect-square">
                  <img
                    src={personalityImage}
                    alt="Personality"
                    className="w-auto h-full object-bottom float-right md:-mr-0 ml-10 mb-0"
                  />
                </div>
              </div>
            </div>
            {isAuth && (
              <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-4">
                <ShareButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData[0]}
                  userGUID={userGUID}
                  userData={userData}
                />
                <SendToFriendButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData[0]}
                  userGUID={userGUID}
                  userData={userData}
                  CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
                />
              </div>
            )}

            {isVisible && (
              <DisclaimerPopup
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                handleTestClick={handleTestClick}
                t={t}
              />
            )}

            <div className="p-6 sm:p-10">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full">
                  <h2
                    style={{ color: personalityColorDark }}
                    className={`text-3xl md:text-4xl font-bold mb-6 `}
                  >
                    {t("report.career_test.overview")}
                  </h2>
                  {insightfulContent &&
                    insightfulContent?.content?.Overview.map((item) => {
                      return <div className="my-6">{item}</div>;
                    })}
                </div>
              </div>
              <div className="mt-8">
                <h2
                  style={{ color: personalityColorDark }}
                  className={`text-3xl md:text-4xl font-bold mb-6 `}
                >
                  {t("report.career_test.recommended_industries")}
                </h2>
                <div className="flex flex-wrap justify-center gap-6">
                  {insightfulContent?.content?.Industries.map((item) => {
                    const industryImage = getIndustryImage(
                      convertToUnderscore(item.EnglishCode)
                    );

                    return (
                      <div className="w-full md:w-[calc(33.333%-1rem)] bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center">
                        <div className="w-16 h-16 text-purple-600 mb-4">
                          <img
                            src={industryImage}
                            alt="Industry"
                            style={{
                              filter: personalityColorStyle,
                            }}
                            className={`w-full h-full object-cover `}
                          />
                        </div>
                        <h3
                          style={{ color: personalityColorDark }}
                          className={`text-xl font-bold mb-2 `}
                        >
                          {item.Industry}
                        </h3>
                        <p className="text-gray-600 mb-4">{item.Content}</p>
                        <button
                          onClick={() => {
                            handleViewMore(item.EnglishCode);
                          }}
                          className="mt-auto bg-insightful-orange hover:bg-insightful-orange text-white font-bold uppercase py-2 px-4 rounded-full transition duration-300 cursor-pointer"
                        >
                          {t("report.career_test.learn_more_cta")}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CareerPrecontent;
